import { LucideIcon } from 'lucide-react'
import { Video, Code, GraduationCap, Briefcase, Calendar } from 'lucide-react'

export interface Project {
  id: string
  title: string
  subtitle: string
  category: 'video' | 'apps' | 'ux' | 'ai'
  icon: LucideIcon
  thumbnail: string
  heroImage: string
  videoUrl?: string
  gallery?: string[]
  tags: string[]
  story: {
    intentie: {
      title: string
      description: string
    }
    proces: {
      title: string
      description: string
      tools?: string[]
    }
    resultaat: {
      title: string
      description: string
      metrics?: string[]
    }
  }
  systemContext: {
    pillar: string
    connection: string
  }
}

export const projects: Project[] = [
  {
    id: 'meester-djay',
    title: 'Meester djay',
    subtitle: 'Adaptief Leerplatform voor het Basisonderwijs',
    category: 'apps',
    icon: GraduationCap,
    thumbnail:
      'https://cdn.magicpatterns.com/uploads/b9aVRivgiucaoKFBynE5JX/Gemini_Generated_Image_vntnstvntnstvntn.png',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/b9aVRivgiucaoKFBynE5JX/Gemini_Generated_Image_vntnstvntnstvntn.png',
    videoUrl:
      'https://cdn.magicpatterns.com/uploads/aVWxcpJoeE6tgynnyarTNa/Schoolapp_Promo_Video_For_Testers.mp4',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/6NWfy8mxRUCFE72yW12JzP/Scherm%C2%ADafbeelding_2026-04-18_om_18.32.24.png',
      'https://cdn.magicpatterns.com/uploads/dT8jt4MdNHQoxh1ZpM6HyP/Scherm%C2%ADafbeelding_2026-04-18_om_18.31.43.png',
    ],
    tags: ['React', 'TypeScript', 'DDD', 'Education', 'AI'],
    story: {
      intentie: {
        title: 'Adaptief Leren op Schaal',
        description:
          'Een volledig functioneel, adaptief oefenplatform voor leerlingen in groep 6–8. Het doel was om een systeem te bouwen dat zich aanpast aan het niveau van elk kind, met diepgaande didactische onderbouwing en een feilloze UX.',
      },
      proces: {
        title: 'Domain-Driven Design & AI-Assisted Engineering',
        description:
          'Gebouwd met een strikte DDD-architectuur (11 domeinen, 0 deep import violations). Ik implementeerde een Learning Dependency Graph (LDG) met 263 vraagfamilies en een 6-stage Governance Pipeline voor content validatie. Dit alles werd gerealiseerd met Claude AI als pair programmer.',
        tools: [
          'React',
          'TypeScript',
          'Tailwind',
          'Framer Motion',
          'Claude AI',
        ],
      },
      resultaat: {
        title: 'Production-Ready Platform',
        description:
          'Een robuust platform met 10 vakgebieden, 24 vraagtypes en multi-rol portalen (leerling, ouder, docent). Het systeem bevat een adaptieve engine met mastery-gebaseerde vraagselectie en contextbewuste feedback.',
        metrics: [
          '263 vraagfamilies',
          '16-tier difficulty system',
          '0 as any casts',
        ],
      },
    },
    systemContext: {
      pillar: 'Full-Stack Ownership',
      connection:
        'Dit project demonstreert mijn capaciteit om complexe, data-gedreven applicaties van A tot Z te ontwerpen en bouwen met een AI-native workflow.',
    },
  },
  {
    id: 'ai-video',
    title: 'AI Video Generation',
    subtitle: 'Character-Driven Storytelling',
    category: 'video',
    icon: Video,
    thumbnail:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    heroImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    videoUrl:
      'https://drive.google.com/file/d/14ZHULrGyJQkKQKuUvvja7mO2PQ8pq4y4/preview',
    tags: ['AI', 'Video', 'Storytelling', 'Character Systems'],
    story: {
      intentie: {
        title: 'Van Concept naar Verhaal',
        description:
          'Hoe gebruik je AI om consistente, character-driven video content te maken? Het begon met de vraag: kunnen we gepersonaliseerde verhalen vertellen zonder elke frame handmatig te animeren? Deze karakters - Cyan en Violet - zijn het resultaat van systematische prompt engineering en character consistency workflows.',
      },
      proces: {
        title: 'Character Systems & Prompt Engineering',
        description:
          'Ik bouwde een character system: eerst definieer je wie je personages zijn (uiterlijk, persoonlijkheid, kleding), dan gebruik je dat als template voor alle scenes. Cyan met haar vibrante blauwe haar en Violet met haar mysterieuze paarse look werden consistent gegenereerd door gestructureerde prompts. Elke scene behoudt character consistency door het systeem.',
        tools: [
          'Midjourney',
          'Runway ML',
          'Custom Prompts',
          'Character Templates',
        ],
      },
      resultaat: {
        title: 'Schaalbare Video Productie',
        description:
          'Het resultaat: een workflow waarmee je in uren video content maakt die normaal dagen kost. De character consistency is hoog, en het verhaal blijft coherent omdat het systeem de rode draad bewaart. Cyan en Violet kunnen nu in oneindig veel scenarios worden gebruikt.',
        metrics: [
          '3 unique characters',
          '10+ scenes generated',
          '90% consistency',
        ],
      },
    },
    systemContext: {
      pillar: 'AI Video',
      connection:
        'Dit project demonstreert mijn AI Video Generation pillar: creative storytelling met systematische AI-integratie en character consistency.',
    },
  },
  {
    id: 'bulkr',
    title: 'BULKR App Design',
    subtitle: 'AI-Driven Shopping Experience',
    category: 'apps',
    icon: Code,
    thumbnail:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop',
    heroImage:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&h=800&fit=crop',
    tags: ['React', 'AI', 'UX', 'E-commerce'],
    story: {
      intentie: {
        title: 'Smart Shopping, Simpel Gemaakt',
        description:
          'De vraag: hoe maak je bulk-shopping intuïtief? Gebruikers willen snel grote hoeveelheden bestellen, maar bestaande apps zijn clunky. AI kan helpen met aanbevelingen en voorspellingen.',
      },
      proces: {
        title: 'UX Research → Rapid Prototyping',
        description:
          'Eerst user research: wat frustreert gebruikers? Dan schetsen, wireframes, en snel prototypen met React. AI-integratie voor slimme suggesties op basis van koopgedrag. Iteratief testen en verfijnen.',
        tools: ['Figma', 'React', 'TypeScript', 'AI API'],
      },
      resultaat: {
        title: 'Intuïtieve Shopping Flow',
        description:
          'Een app die voelt als magie: je begint met typen, AI suggereert producten, en binnen 3 taps ben je klaar. Snelheid en simpliciteit als kernwaarden.',
        metrics: ['3-tap checkout', '40% sneller', 'AI suggesties'],
      },
    },
    systemContext: {
      pillar: 'VibeCoding',
      connection:
        'Dit project toont mijn VibeCoding aanpak: van UX thinking naar werkende app in dagen, niet maanden.',
    },
  },
  {
    id: 'jdg-advies',
    title: 'JDG Advies',
    subtitle: 'AI-Powered Website voor Financieel Adviseur',
    category: 'apps',
    icon: Briefcase,
    thumbnail:
      'https://cdn.magicpatterns.com/uploads/vFJXrhRW7uhYauVuoPpbgy/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.23.png',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/vFJXrhRW7uhYauVuoPpbgy/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.23.png',
    tags: ['Web Design', 'AI Generation', 'UX', 'Finance'],
    story: {
      intentie: {
        title: 'Persoonlijk en Vertrouwd',
        description:
          'Een toegankelijke, betrouwbare online aanwezigheid creëren voor een financieel adviseur, waarbij persoonlijk contact centraal staat.',
      },
      proces: {
        title: 'AI-Versneld Design',
        description:
          'Gebruik gemaakt van AI voor snelle iteraties in design en content generatie. Focus op een heldere conversie-flow ("Plan een gratis kennismaking") en een overzichtelijke navigatie.',
      },
      resultaat: {
        title: 'Direct Actiegericht',
        description:
          'Een strakke, moderne website die vertrouwen uitstraalt en direct aanzet tot actie, gebouwd in een fractie van de normale ontwikkeltijd dankzij AI-tools.',
      },
    },
    systemContext: {
      pillar: 'VibeCoding',
      connection:
        'Dit project laat zien hoe AI kan worden ingezet om snel professionele, conversiegerichte websites te realiseren.',
    },
  },
  {
    id: 'vvgn-platform',
    title: 'VVGN Platform',
    subtitle: 'Complex Planningssysteem Dashboard',
    category: 'apps',
    icon: Calendar,
    thumbnail:
      'https://cdn.magicpatterns.com/uploads/eZaAPHvS4TzHce8t3mDTdS/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.12.png',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/eZaAPHvS4TzHce8t3mDTdS/Scherm%C2%ADafbeelding_2026-04-18_om_20.33.12.png',
    tags: ['Dashboard', 'React', 'UX', 'Planning'],
    story: {
      intentie: {
        title: 'Overzicht in Complexiteit',
        description:
          'Het overzichtelijk maken van complexe planningsdata voor instructeurs. Van onoverzichtelijke lijsten naar een intuïtief dashboard voor aanwezigheidsregistratie.',
      },
      proces: {
        title: 'Terug naar de Essentie',
        description:
          'Ruis weghalen en logica toevoegen. Complexe data-structuren vertaald naar heldere UI-componenten in een React-omgeving, met een focus op de primaire taak: registreren.',
      },
      resultaat: {
        title: 'Intuïtieve Workflow',
        description:
          'Een overzichtelijk dashboard waar gebruikers direct zien wat ze moeten doen. Minder kliks, minder fouten, en een systeem dat gewoon "fijn voelt".',
      },
    },
    systemContext: {
      pillar: 'UX & Engineering',
      connection:
        'Een perfect voorbeeld van mijn aanpak: complexe domeinen (planning) vertalen naar een logische, schaalbare interface.',
    },
  },
]
